import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Chip,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L, { Marker as LeafletMarker } from "leaflet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faChevronDown,
    faCoins,
    faFutbol,
    faHandHoldingHeart,
    faLeaf,
    faRecycle,
    faRoad,
    faSchool,
    faShieldHalved,
    faUsers,
    faVoteYea,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import SectionTitle from "@others/title-section.layout";

// @ts-ignore
import candidatePhoto from "../assets/images/others/jpg.png";
// @ts-ignore
import vldLogo from "../assets/images/logo/vld-2026.png";
// @ts-ignore
import groupPhoto from "../assets/images/others/photo-vld-groupe.jpeg";
// @ts-ignore
import projectMiseEnSeine from "../assets/images/others/home-1.jpg";
// @ts-ignore
import projectRespire from "../assets/images/others/home-2.jpg";

type LatLng = [number, number];

interface ProgramSection {
    title: string;
    color: string;
    icon: IconDefinition;
    lines?: string[];
    image?: {
        src: string;
        alt: string;
        caption: string;
    };
}

interface CampaignPoint {
    id: number;
    title: string;
    position: LatLng;
    description: string;
}

const palette = {
    blue: "#1F4E79",
    blueDark: "#14344F",
    blueSoft: "#ECF3FB",
    green: "#2E7D32",
    greenSoft: "#EDF7EE",
    orange: "#EF6C00",
    orangeSoft: "#FFF2E6",
    text: "#1F2D3D",
};

const programSections: ProgramSection[] = [
    {
        title: "Qualité & Cadre de vie",
        color: palette.green,
        icon: faLeaf,
        lines: [
            "Nos engagements :",
            "1. Veiller à une commune propre et soignée",
            "− Renforcer la propreté des rues.",
            "− Créer des îlots fleuris pour embellir les quartiers.",
            "− Entretenir les parties communales, notamment du quai de la Ruelle.",
            "2. Agir auprès de l'agglomération pour rétablir la collecte des déchets verts sur une période élargie et qui correspond aux travaux de saison dans nos jardins.",
            "3. Réaménager et arborer la place de la Cité.",
            "4. Mieux protéger les riverains du stade des nuisances sonores.",
            "5. Obtenir le label Site Patrimonial Remarquable pour Bois-le-Roi. Ce label permettra de préserver ce qui fait le caractère de Bois-le-Roi : l’architecture, les paysages urbains, les alignements d’arbres, les perspectives, le lien entre le bâti et la forêt.",
        ],
    },
    {
        title: "Sécurité",
        color: "#3E8E5A",
        icon: faShieldHalved,
        lines: [
            "Notre engagement : améliorer la sécurité partout sur la commune.",
            "Nos actions :",
            "1. Rejoindre le Conseil Intercommunal de la Sécurité et de la Prévention de la Délinquance, et œuvrer pour l’ouverture d’un bureau de Police sur l'île de loisirs.",
            "2. Renforcer les patrouilles de la police municipale et leur présence sur le terrain.",
            "3. Optimiser l’éclairage public pour répondre aux besoins conjoints de sécurité et de biodiversité, en concertation avec les associations et les habitants.",
            "4. Prévenir les cambriolages avec des actions ciblées et citoyennes.",
            "5. Restaurer la tranquillité publique près de la Gare et de l’île de Loisirs. Formaliser une convention de coordination avec la Police Nationale. Élargir la plage horaire des navettes afin de correspondre aux horaires d’ouverture de l’île de Loisirs.",
            "6. Remplacer nos caméras de vidéoprotection obsolètes avec le soutien de la Région.",
        ],
    },
    {
        title: "Urbanisme, logement & aménagement du territoire",
        color: palette.blue,
        icon: faBuilding,
        lines: [
            "Nous modifierons le PLUi, dans un cadre légal et encadré, mais avec une vraie concertation des Bacots.",
            "L’urbanisme doit être choisi par ses habitants et donc équilibré, pas imposé.",
            "Nos 3 engagements :",
            "1. Rétablir le droit de propriété sur toute la commune.",
            "Nous supprimerons les secteurs de mixité sociale de type 2 qui imposent 80% de logements sociaux, dès le premier logement réalisé, et empêchent les propriétaires concernés de bâtir leur propre maison sur leur terrain.",
            "2. Protéger Bois-le-Roi d’une densification.",
            "Nous maintiendrons les secteurs de mixité sociale de type 1 pour empêcher les constructions de collectifs privés.",
            "Cette règle protégera Bois-le-Roi, et lui évitera de devenir une ville comme Saint Fargeau-Ponthierry (la règle de SMS 1 aurait empêché la construction du futur immeuble de la rue des Sesçois).",
            "3. Mieux protéger nos espaces naturels et la biodiversité.",
            "Logements sociaux : une stratégie responsable pour Bois-le-Roi.",
            "Notre engagement : agir pour une politique de logement social adaptée aux capacités de la commune, tout en évitant un arrêté de carence du préfet.",
            "Notre choix : une stratégie ciblée et maîtrisée. Réduire notre déficit en LS, tout en protégeant Bois-le-Roi, ses espaces naturels, son patrimoine et le droit de propriété.",
            "1. Préserver notre cadre de vie.",
            "− Priorité à la rénovation et à la transformation de bâtiments existants, en particulier les maisons sans maîtres (i.e. sans propriétaire identifié par les Services fiscaux).",
            "− Utilisation du foncier public et des sites déjà artificialisés.",
            "− Construire en dernier recours de petits collectifs intégrés et diffus.",
            "− Encourager au conventionnement avec l'Agence Nationale de l'Amélioration de l'Habitat.",
            "2. Privilégier les petites structures collectives.",
            "− Retraités, avec des logements adaptés (ex: aménagement de l’ex clinique Chantemerle).",
            "− Étudiants, réaliser une résidence étudiante de petite taille pendant la mandature.",
            "Nous refusons un développement subi : une croissance démographique non maîtrisée dégraderait rapidement notre cadre de vie et imposerait de lourds investissements : infrastructures (réseaux d'assainissement, voirie), équipements (écoles), et services communaux.",
        ],
    },
    {
        title: "Finances",
        color: "#2C6EA5",
        icon: faCoins,
        lines: [
            "Notre engagement : retrouver des finances saines et mettre fin à la hausse de la part communale des impôts fonciers.",
            "Une commune bien gérée, c'est une commune qui peut investir, innover et soutenir ses habitants.",
            "Faisons de la gestion communale un levier d'avenir !",
            "Nos actions :",
            "1. Stopper l’hémorragie des dépenses et rétablir l'équilibre financier.",
            "− Audit interne complet des Finances.",
            "− Renégocier les principales dépenses récurrentes (énergie, contrats de prestations, abonnements…), dans un contexte où chaque euro compte.",
            "− Mutualiser certaines dépenses avec les communes de la CAPF, pour bénéficier d’économies d’échelle et d’une gestion plus intelligente à l’échelle du territoire.",
            "− Mise en place d'un plan de réduction des charges de fonctionnement.",
            "− Mise en place d'un plan de désendettement réaliste et suivi annuel des progrès.",
            "− Mutualisation de certains services avec les communes voisines.",
            "2. Mettre le bien-être au travail au cœur des services de la commune.",
            "− Rétablir le bien-être au travail pour réduire le taux anormalement élevé d’absences pour raisons de santé.",
            "− Engager un plan « qualité de vie au travail » et une mesure de l’impact positif du service public rendu aux habitants.",
            "− Remplacer les départs à la retraite au cas par cas pour retrouver sur le moyen terme un effectif cohérent avec notre strate.",
            "3. Remettre de la transparence dans les finances.",
            "− Réunion publique de présentation des comptes chaque année.",
            "− Suivi budgétaire accessible en ligne et affiché en mairie.",
            "− Mettre à disposition de chacun des documents administratifs de la commune (obligation légale non respectée à ce jour).",
            "4. Investir utile, ne pas dépenser plus.",
            "− Recherche active de financements externes (Europe, État, Région, etc.), participatifs et de mécénat.",
            "− Investir pour réduire les charges de fonctionnement (isolation des bâtiments publics, remplacement de la bulle de tennis).",
            "− Montage de projets avec des partenariats public/privé (projet photovoltaïque de couverture des terrains de tennis) pour réduire les coûts et l’empreinte énergétique.",
            "− Priorité à l'enfance, au cadre de vie, au sport et à la transition écologique et énergétique.",
            "− Entretien et valorisation du patrimoine existant avant de lancer de nouveaux projets.",
        ],
    },
    {
        title: "Sport",
        color: palette.orange,
        icon: faFutbol,
        lines: [
            "À Bois-le-Roi, le sport est bien plus qu’une activité physique : c’est le ciment de notre communauté et le moteur du bien-être.",
            "Nos actions :",
            "1. Le Gymnase : Notre Priorité.",
            "Nos infrastructures sont à bout de souffle. Dès le début du mandat, une structure modulaire sera installée avant d'envisager une structure pérenne et qu'aucune famille ne soit refusée. Mettons fin à l'exil de nos sportifs !",
            "2. Énergie et Modernisation : Le Tennis.",
            "La « bulle » actuelle, en fin de vie, est un non-sens économique et écologique. Nous la remplacerons par une structure pérenne et solaire. Ce projet transforme un coût énergétique en une ressource, tout en offrant aux joueurs des conditions de jeu optimales toute l’année.",
            "3. Football.",
            "Nous relancerons les négociations avec l’agglomération pour aménager un terrain en herbe aux Foucherolles.",
            "4. L’Espace Hybride : Un Boulodrome Multisports.",
            "Nous étudierons en concertation avec les associations concernées la couverture du boulodrome par une toiture photovoltaïque. Ce site hybride pourra accueillir d’autres activités (tir à l’arc, yoga...), permettant de libérer des créneaux dans nos salles municipales pour d’autres associations. Nous aménagerons également une surface de stockage fonctionnelle adaptée aux besoins de tous.",
            "5. Sport-Santé et Cohésion.",
            "Le sport sera un moteur de bien-être pour nos aînés et d'épanouissement pour notre jeunesse. Une Fête du Sport annuelle mettra à l'honneur l'engagement précieux de nos bénévoles.",
        ],
    },
    {
        title: "Culture et Vie associative",
        color: "#F28D2B",
        icon: faUsers,
        lines: [
            "Notre engagement : Développer les moments de convivialité pour les rendre plus attractifs, festifs et fédérateurs.",
            "Nos actions :",
            "1. Retrouver notre âme de village.",
            "Lors des fêtes, marchés, rendez-vous saisonniers, animations dans nos quartiers. Faire vivre nos espaces publics et recréer du lien entre les générations.",
            "2. Dynamiser nos événements existants.",
            "Cinéma en plein air, théâtre de verdure, festival des Briardises, etc.",
            "3. Valoriser la création locale.",
            "Offrir des espaces d’expression aux artistes et talents du territoire : expositions, scènes ouvertes, ateliers participatifs.",
            "4. Soutenir nos associations.",
            "Simplifier leurs démarches, améliorer l’accès aux salles et aux équipements, renforcer la communication municipale et accompagner les projets.",
            "Notre projet « Mise en Seine ».",
            "Et si la Seine devenait un véritable lieu de vie ?",
            "Nous proposons l’installation d’une péniche dédiée aux activités culturelles et festives, amarrée sur notre commune.",
            "Située à proximité de l'île de loisirs, elle sera respectueuse de l’environnement et de la tranquillité des riverains.",
            "Un espace chaleureux, ouvert à tous, qui accueillerait :",
            "− Concerts, spectacles, théâtre.",
            "− Guinguettes.",
            "− Expositions et ateliers artistiques.",
            "− Événements associatifs.",
            "− Mariages, anniversaires, réceptions.",
        ],
        image: {
            src: projectMiseEnSeine,
            alt: "Projet Mise en Seine",
            caption: "Projet « Mise en Seine » (image à remplacer).",
        },
    },
    {
        title: "Environnement & transition écologique",
        color: palette.green,
        icon: faRecycle,
        lines: [
            "Nos actions :",
            "1. Une eau protégée et préservée.",
            "− Exiger de l’agglomération (CAPF), qui détient la compétence “eau potable”, de traiter à la source pour délivrer dans notre commune une eau de qualité, sans perchlorates ni pesticides, avec publication transparente des analyses.",
            "− Lancer un plan de désimperméabilisation des sols pour préserver le cycle naturel de l’eau.",
            "2. Sobriété et énergie verte.",
            "− Négocier un contrat cadre d’énergie décarbonée compétitive pour les habitants.",
            "− Auditer et renforcer l’isolation des bâtiments communaux.",
            "− Installer des panneaux solaires sur certains équipements publics.",
            "3. Protéger notre nature et notre biodiversité.",
            "− Adopter une charte de l’arbre et protéger les arbres remarquables, favoriser la plantation de haies diversifiées.",
            "− Replanter pour créer des îlots de fraîcheur et suivre l’indice de canopée.",
            "− Sanctuariser le Bois de la Source.",
            "− Restaurer les sentes.",
            "− Répertorier et préserver les sources, rûs et mares.",
            "4. Faire du PLUi un outil de protection de la biodiversité.",
            "− Renforcer les corridors écologiques et les trames vertes et bleues.",
            "− Prévenir les risques naturels : argile, inondations, glissements de terrain.",
            "− Revoir le zonage des Espaces Boisés Classés.",
            "Notre projet « Respire ! ».",
            "Dans le parc de Chardonnerets, la maison des associations a vocation à évoluer pour devenir la Maison des associations et de la Biodiversité.",
            "Son parc sera protégé et aménagé dans l'esprit d'un arboretum.",
            "Ce nouvel ensemble accueillera une salle d’exposition permanente dédiée à la nature et au vivant, ainsi qu’un espace ouvert aux associations locales. Il pourra également proposer des ateliers de sensibilisation à la biodiversité, à la petite faune et aux pratiques de jardinage respectueuses de l’environnement, des actions pédagogiques à destination des enfants, ainsi que des appels à projets pour encourager les initiatives citoyennes.",
        ],
        image: {
            src: projectRespire,
            alt: "Projet Respire",
            caption: "Projet « Respire ! » (image à remplacer).",
        },
    },
    {
        title: "Education enfance & jeunesse",
        color: "#F39F3E",
        icon: faSchool,
        lines: [
            "À Bois-le-Roi, les enfants doivent pouvoir grandir et s'épanouir dans un environnement verdoyant, sûr et attentif à leurs besoins.",
            "1. Petite enfance.",
            "− Installer une micro-crèche privée avec 12 nouvelles places, dans un lieu déjà artificialisé, près des écoles, sans subvention municipale.",
            "− Réaliser une Maison d’Assistantes Maternelles pouvant accueillir jusqu’à 16 enfants.",
            "2. Restauration scolaire.",
            "− Lancer une étude de faisabilité avec les associations de parents d’élèves et l’agglomération afin de proposer des repas issus de l’agriculture locale en augmentant la part du Bio dans les assiettes. Nous limiterons la nourriture perdue et donnerons davantage de travail à nos agriculteurs locaux. Le prix des repas ne devra pas être augmenté.",
            "3. Centre de loisirs.",
            "− Encourager les projets d’animation co-construits entre le centre de loisirs et les associations.",
            "− Apporter une attention particulière aux animateurs, à leur embauche, et les former régulièrement sur la lutte contre le harcèlement et la gestion des conflits.",
            "4. École Primaire.",
            "− Pendant la mandature, créer un maximum de 3 nouvelles salles qui peuvent être utilisées selon les besoins par l’Éducation Nationale, le Centre de Loisirs, ou des associations.",
            "− Améliorer l’isolation des classes et créer un abri, inexistant actuellement, pour les récréations pluvieuses.",
            "− Corriger les dysfonctionnements de l’aménagement de la cour de l’école O Métra.",
        ],
    },
    {
        title: "Solidarités, santé et séniors",
        color: "#E78129",
        icon: faHandHoldingHeart,
        lines: [
            "Nos engagements :",
            "1. Œuvrer pour l’installation de 3 nouveaux médecins sans patientèle déjà établie. L'accès à la santé est un besoin essentiel et un pilier du bien-être collectif.",
            "2. Lutter contre l’isolement et favoriser le bien vieillir.",
            "Développer les visites de convivialité, les cafés séniors et les ateliers « bien vieillir » (mémoire, prévention des chutes, numérique, nutrition). Encourager les actions intergénérationnelles pour maintenir le lien social et le maintien à domicile.",
            "3. Renforcer l’action sociale de proximité pour les personnes défavorisées.",
            "Rendre le CCAS plus visible et plus accessible : meilleure information sur les aides existantes, accompagnement dans les démarches administratives (CAF, retraite, santé), suivi individualisé des situations fragiles.",
            "4. Soutenir les aidants et les personnes en situation de handicap, en collaboration avec les associations.",
        ],
    },
    {
        title: "Démocratie locale et participative",
        color: palette.blue,
        icon: faVoteYea,
        lines: [
            "La démocratie participative est la clé de voûte de notre programme.",
            "1. Structurer la Démocratie Participative.",
            "− Tenir des réunions publiques régulières.",
            "− Organiser une réunion publique annuelle sur les finances.",
            "− Ouvrir aux listes d'opposition, le site internet et l'application de la commune.",
            "2. Déployer des Outils Concrets.",
            "− Créer un espace numérique communal de démarches et de consultations sécurisées.",
            "− Vous consulter sur tous les projets structurants, et si besoin recourir au référendum.",
            "3. Impliquer les Bacots dans les projets locaux.",
            "− Informer à chaque étape des projets, via le site internet de la commune et lors de réunions publiques régulières.",
            "− Garantir qu’aucun projet structurant ne démarre sans une concertation préalable avec l’ensemble des personnes concernées.",
            "4. Évaluer et Ajuster nos Démarches.",
            "− Nommer une commission “démocratie et déontologie” avec l’opposition, qui devra notamment réaliser un bilan annuel sur le fonctionnement démocratique de la commune.",
        ],
    },
    {
        title: "Mobilité et voirie",
        color: palette.green,
        icon: faRoad,
        lines: [
            "Notre engagement : retravailler avec toutes les parties prenantes un plan de mobilité qui respecte chaque mode de déplacement (voiture, vélo, piéton) et facilite l’accessibilité des personnes à mobilité réduite.",
            "Nos actions :",
            "1. Entretenir correctement voiries, trottoirs et équipements.",
            "2. Création de cheminements piétons et cyclables continus et lisibles.",
            "3. Optimiser le stationnement résidentiel et aux abords des commerces.",
            "4. Accessibilité PMR améliorée et intégrée à chaque projet.",
            "5. En collaboration avec la SNCF, aménager une liaison douce de Brolles à la gare, le long de la ligne de chemin de fer dans le prolongement de la rue de Bellevue.",
        ],
    },
];

const campaignPoints: CampaignPoint[] = [
    {
        id: 1,
        title: "Projet Sport - Stade Langenargen",
        position: [48.4710, 2.6965],
        description: "Moderniser les infrastructures sportives pour offrir de meilleures conditions d’entraînement toute l’année. Notre objectif est de développer les capacités d’accueil afin qu’aucune famille ne soit refusée.",
    },
    {
        id: 2,
        title: "Projet Sport - Stade des foucherolles",
        position: [48.4750, 2.7154],
        description: "Relancer les négociations avec l’agglomération pour aménager un terrain en herbe aux Foucherolles. Ce projet répond à un besoin concret des clubs et des pratiquants.",
    },
    {
        id: 3,
        title: "Projet Sport - Boulodrome",
        position: [48.4815, 2.7071],
        description: "Étudier la couverture du boulodrome par une toiture photovoltaïque en concertation avec les associations. L’espace pourra accueillir d’autres activités et libérer des créneaux municipaux.",
    },
    {
        id: 4,
        title: 'Projet "Mise en Seine" - La péniche',
        position: [48.4830, 2.7100],
        description: "Installer une péniche dédiée aux activités culturelles et festives, ouverte à tous. Le projet sera situé près de l’île de loisirs, dans le respect de l’environnement et des riverains.",
    },
    {
        id: 5,
        title: "Sécuriser les abords de l'île de loisirs",
        position: [48.4815, 2.7122],
        description: "Restaurer la tranquillité publique autour de la gare et de l’île de loisirs avec une coordination renforcée Police municipale / Police nationale. Adapter aussi les horaires de navettes aux horaires d’ouverture du site.",
    },
    {
        id: 6,
        title: "Création d'une micro-crèche",
        position: [48.4722, 2.7009],
        description: "Créer une micro-crèche privée de 12 places, près des écoles, sur un site déjà artificialisé. L’objectif est d’augmenter l’offre de garde sans subvention municipale.",
    },
    {
        id: 7,
        title: 'Projet "Respire" - Maison et parc de la biodiversité',
        position: [48.4737, 2.6935],
        description: "Faire évoluer la maison des associations en Maison des associations et de la Biodiversité. Le parc sera protégé dans l’esprit d’un arboretum avec des actions pédagogiques pour tous.",
    },
    {
        id: 8,
        title: "Réaménager et arborer la place de la Cité",
        position: [48.4682, 2.7074],
        description: "Réaménager la place de la Cité pour en faire un espace plus agréable, ombragé et vivant. Ce projet s’inscrit dans l’objectif global d’amélioration du cadre de vie.",
    },
    {
        id: 9,
        title: "Liaison douce piétons / vélos de Brolles à la gare",
        position: [48.4780, 2.6875],
        description: "Créer une liaison douce continue et lisible entre Brolles et la gare, en partenariat avec la SNCF. Cette action améliore les déplacements du quotidien pour les piétons, les vélos et les PMR.",
    },
    {
        id: 10,
        title: "Aménager un chemin piéton de Brolles à la Seine",
        position: [48.4830, 2.6800],
        description: "Développer un cheminement piéton sécurisé reliant les quartiers aux bords de Seine. L’objectif est de mieux partager l’espace public et de faciliter les mobilités douces.",
    },
    {
        id: 11,
        title: "Entretenir le quai de la Ruelle, mettre en valeur le parcours bacot de la Scandibérique",
        position: [48.4820, 2.6835],
        description: "Renforcer l’entretien des parties communales, notamment du quai de la Ruelle. Mettre en valeur le parcours bacot de la Scandibérique dans une logique de patrimoine et de qualité de vie.",
    },
    {
        id: 12,
        title: "Obtenir le label Site Patrimonial Remarquable",
        position: [48.4810, 2.6945],
        description: "Obtenir le label Site Patrimonial Remarquable pour préserver l’architecture, les perspectives et les alignements d’arbres. Un levier fort pour protéger l’identité de village en forêt.",
    },
];

const mapCenter: LatLng = [48.476, 2.704];

const createPointIcon = (id: number, isActive: boolean): L.DivIcon =>
    L.divIcon({
        className: "campaign-map-marker",
        html: `
            <div style="
                width: ${isActive ? 30 : 26}px;
                height: ${isActive ? 30 : 26}px;
                border-radius: 50%;
                background: ${isActive ? palette.blue : palette.orange};
                color: white;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                font-weight: 700;
            ">${id}</div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
    });

interface FlyToSelectedPointProps {
    position: LatLng;
}

const FlyToSelectedPoint: React.FC<FlyToSelectedPointProps> = ({ position }) => {
    const map = useMap();

    useEffect(() => {
        map.flyTo(position, 14.7, { duration: 0.7 });
    }, [map, position]);

    return null;
};

const orderedColistiers = [
    "1. Jean-Philippe Guibert - Photographe, ancien adjoint aux affaires scolaires et périscolaires",
    "2. Laurence Gravier - Notaire",
    "3. Cyril Parussolo - Manager opérationnel informatique",
    "4. Cécile Faragoni - Cheffe de service ingénierie eau potable",
    "5. Fabrice Martinez - Dirigeant de société",
    "6. Charlotte Gachon - Comédienne",
    "7. Xavier Blondaz-Gérard - Responsable Marketing",
    "8. Laurence Chammah - Greffier principal",
    "9. Philippe Lefort - Retraité du ministère de l'intérieur",
    "10. Séverine Gopauloo - Directrice d'agence bancaire à la retraite",
    "11. Jean-Pierre Morin - Cadre dirigeant",
    "12. Clarisse Perret - Responsable formation / marketing en pré retraite",
    "13. Paul Walther - Consultant finances",
    "14. Madison Détré - Étudiante en journalisme",
    "15. Cyril Lamirand - Ingénieur informatique",
    "16. Jacqueline Megret-Petit - Retraitée de l'Éducation Nationale",
    "17. Jean-Jacques Van Poucke - Chef d'entreprise plomberie (retraité)",
    "18. Isabelle Lacoste - Infirmière à la retraite",
    "19. François Perret - Responsable de projets informatiques à la retraite",
    "20. Stéphanie Cornette - Enseignante",
    "21. Cyril Radici - Directeur Général d'une Organisation Professionnelle",
    "22. Fabienne Vier - Comptable et aide familiale",
    "23. Mayeul Buisson - Responsable qualité",
    "24. Anne Sophie Jacouty - Ecrivain",
    "25. François Dugain - Retraité (Conducteur de travaux)",
    "26. Babeth Aloy - Photojournaliste",
    "27. Stéphane Beix - Commercial",
    "28. Sylvie Carrier - Retraitée (responsable RH)",
    "29. Stéphane Kidjo - Entrepreneur solution IA",
    "30. Stéphanie Diot - Responsable service ingénieurs aéronautiques",
    "31. François Bonnin - Directeur Financier",
];

const parseColistier = (item: string) => {
    const match = item.match(/^(\d+)\.\s*(.*)$/);
    if (!match) return { rank: 0, text: item };
    return { rank: Number(match[1]), text: match[2] };
};

const CampaignPage: React.FC = () => {
    const [activePointId, setActivePointId] = useState<number>(campaignPoints[0].id);
    const markerRefs = useRef<Record<number, LeafletMarker | null>>({});

    const activePoint = useMemo(
        () => campaignPoints.find((point) => point.id === activePointId) ?? campaignPoints[0],
        [activePointId]
    );

    const handleSelectPointFromList = (pointId: number) => {
        setActivePointId(pointId);
        window.setTimeout(() => {
            markerRefs.current[pointId]?.openPopup();
        }, 220);
    };

    return (
        <Container sx={{ pb: 8 }}>
            <SectionTitle title="PROGRAMME MUNICIPAL 2026" />

            <Paper
                elevation={3}
                sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    background: `linear-gradient(120deg, ${palette.blueDark} 0%, ${palette.blue} 45%, ${palette.green} 100%)`,
                    color: "white",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Typography variant="h3" fontWeight={800}>
                            Bois-le-Roi avance avec vous
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 1.5, opacity: 0.95 }}>
                            Un cap clair, des priorités concrètes et une équipe de terrain au service de la commune.
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
                            Découvrez les grandes orientations du programme municipal porté par Jean-Philippe Guibert et son équipe.
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 3 }}>
                            <Chip
                                label="Municipales Bois-le-Roi"
                                sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "white" }}
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                            <Box
                                component="img"
                                src={vldLogo}
                                alt="Logo VLD 2026"
                                sx={{
                                    width: { xs: 220, md: 270 },
                                    maxWidth: "100%",
                                    bgcolor: "rgba(255,255,255,0.94)",
                                    p: 1.2,
                                    borderRadius: 2.5,
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    mt: 3,
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    border: `1px solid ${palette.orange}`,
                    bgcolor: palette.orangeSoft,
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <FontAwesomeIcon icon={faVoteYea} color={palette.orange} />
                    <Typography variant="h5" fontWeight={700} color={palette.orange}>
                        Votez le 15 et 22 mars 2026
                    </Typography>
                </Box>
            </Paper>

            <Paper elevation={1} sx={{ mt: 3, p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
                <Grid container spacing={2.5} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid #DDE7F2",
                                bgcolor: "#FAFCFF",
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                    src={candidatePhoto}
                                    alt="Jean-Philippe Guibert"
                                    sx={{
                                        width: { xs: 120, md: 150 },
                                        height: { xs: 120, md: 150 },
                                        border: `3px solid ${palette.blue}`,
                                    }}
                                />
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ color: palette.blue }}>
                                        Jean-Philippe Guibert
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Tête de liste VLD 2026
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: palette.blue }}>
                            Où je veux vous conduire à l'horizon des dix prochaines années ?
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Vous pouvez compter sur moi pour améliorer durablement la qualité de vie à Bois-le-Roi. Cela implique une
                            responsabilité majeure : maîtriser l'évolution démographique de notre commune, car nos infrastructures, nos
                            ressources naturelles et nos capacités de services publics ne sont pas illimitées.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Vous pouvez compter sur moi pour préserver notre identité de village en forêt. Je ferai modifier le PLUi
                            pour faire respecter le droit de propriété, et mieux protéger notre cadre de vie et la biodiversité.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Vous pouvez compter sur moi pour protéger votre patrimoine, garantir votre sécurité et assurer des services
                            publics de qualité.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Le chemin sera difficile, mais cette ligne est ma boussole et je m'y tiendrai.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Vous pouvez compter sur moi pour retrouver du dialogue, de la sérénité et de la confiance. Je sais
                            rassembler les gens autour des projets, en écoutant et en tenant compte de la parole de chacun. Écouter
                            pour mieux décider.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ color: palette.text }}>
                            Le 15 mars, je compte sur vous, mobilisez-vous et mobilisez autour de vous.
                        </Typography>
                        <Typography variant="body1" sx={{ color: palette.text }}>
                            Pour que Bois-le-Roi évolue dans le bon sens.
                        </Typography>

                        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: "1px dashed rgba(31,78,121,0.25)", textAlign: "right" }}>
                            <Typography
                                sx={{
                                    color: palette.blue,
                                    fontSize: { xs: "1.35rem", md: "1.6rem" },
                                    fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                    fontWeight: 600,
                                    lineHeight: 1.1,
                                }}
                            >
                                Jean-Phillipe Guibert
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mt: 6 }}>
                <SectionTitle title="CARTE DES PROJETS" />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box
                            sx={{
                                height: { xs: 420, md: 560 },
                                borderRadius: 3,
                                overflow: "hidden",
                                border: `2px solid ${palette.blue}`,
                            }}
                        >
                            <MapContainer center={mapCenter} zoom={14} style={{ height: "100%", width: "100%" }}>
                                <FlyToSelectedPoint position={activePoint.position} />
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {campaignPoints.map((point) => (
                                    <Marker
                                        key={point.id}
                                        position={point.position}
                                        icon={createPointIcon(point.id, point.id === activePointId)}
                                        ref={(marker) => {
                                            markerRefs.current[point.id] = marker;
                                        }}
                                        eventHandlers={{ click: () => setActivePointId(point.id) }}
                                    >
                                        <Popup>
                                            <Box sx={{ width: 230 }}>
                                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                                    {point.title}
                                                </Typography>
                                                <Typography variant="body2">{point.description}</Typography>
                                            </Box>
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                border: `1px solid #D8E4F1`,
                                bgcolor: "#FAFCFF",
                                p: 2,
                                height: { xs: "auto", md: 560 },
                                overflow: "auto",
                            }}
                        >
                            <Typography variant="h6" fontWeight={700} sx={{ color: palette.blue, mb: 1 }}>
                                12 priorités locales
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Cliquez sur un point pour centrer la carte et ouvrir son contenu.
                            </Typography>
                            <Stack spacing={1}>
                                {campaignPoints.map((point) => (
                                    <Paper
                                        key={point.id}
                                        elevation={0}
                                        onClick={() => handleSelectPointFromList(point.id)}
                                        sx={{
                                            p: 1.25,
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            border: point.id === activePointId
                                                ? `1px solid ${palette.blue}`
                                                : "1px solid #E6EBF2",
                                            bgcolor: point.id === activePointId ? palette.blueSoft : "white",
                                            transition: "all 0.2s ease",
                                            "&:hover": { borderColor: palette.blue },
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            {point.id}. {point.title}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mt: 6 }}>
                <SectionTitle title="LE PROGRAMME" />
                <Stack spacing={1.5}>
                    {programSections.map((section, index) => {
                        const lines = section.lines && section.lines.length > 0
                            ? section.lines
                            : [
                                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer sodales erat non mi dictum, et faucibus purus fermentum.",
                            ];

                        return (
                            <Accordion
                                key={section.title}
                                disableGutters
                                defaultExpanded={index === 0}
                                elevation={0}
                                sx={{
                                    border: "1px solid #E4E9EF",
                                    borderRadius: "14px !important",
                                    background: `linear-gradient(110deg, ${section.color}12 0%, #FFFFFF 55%)`,
                                    "&:before": { display: "none" },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<FontAwesomeIcon icon={faChevronDown} color={section.color} />}
                                    sx={{ px: 2.5, py: 0.5 }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <FontAwesomeIcon icon={section.icon} color={section.color} />
                                        <Typography variant="h6" fontWeight={700}>
                                            {section.title}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, px: 2.5, pb: 2.5 }}>
                                    <Stack spacing={1}>
                                        {lines.map((line, lineIndex) => {
                                            const trimmedLine = line.trim();
                                            const isSubBullet = trimmedLine.startsWith("− ");
                                            const isNumbered = /^\d+\./.test(trimmedLine);
                                            const isStrongLine = !isSubBullet && (
                                                trimmedLine.endsWith(":")
                                                || trimmedLine.startsWith("Nos engagements")
                                                || trimmedLine.startsWith("Notre engagement")
                                                || trimmedLine.startsWith("Nos actions")
                                                || trimmedLine.startsWith("Notre projet")
                                                || trimmedLine.startsWith("Logements sociaux")
                                                || trimmedLine.startsWith("Notre choix")
                                            );

                                            if (isSubBullet) {
                                                return (
                                                    <Box key={`${section.title}-${lineIndex}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                                        <Box sx={{ width: 14, display: "flex", justifyContent: "center", pt: 0.25, flexShrink: 0 }}>
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    color: section.color,
                                                                    fontWeight: 800,
                                                                    lineHeight: 1.2,
                                                                }}
                                                            >
                                                                •
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.62, flex: 1 }}>
                                                            {trimmedLine.slice(2)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }

                                            if (isNumbered) {
                                                const numberMatch = trimmedLine.match(/^(\d+)\.\s*(.*)$/);
                                                const number = numberMatch ? numberMatch[1] : "";
                                                const text = numberMatch ? numberMatch[2] : trimmedLine;

                                                return (
                                                    <Box
                                                        key={`${section.title}-${lineIndex}`}
                                                        sx={{ display: "flex", alignItems: "flex-start", gap: 1.1, mt: 0.5 }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                borderRadius: "50%",
                                                                bgcolor: section.color,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flex: "0 0 auto",
                                                                mt: "2px",
                                                            }}
                                                        >
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    color: "white",
                                                                    fontSize: "0.78rem",
                                                                    fontWeight: 800,
                                                                    lineHeight: 1,
                                                                }}
                                                            >
                                                                {number}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: section.color, pt: 0.25, lineHeight: 1.45 }}>
                                                            {text}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }

                                            if (isStrongLine) {
                                                return (
                                                    <Typography
                                                        key={`${section.title}-${lineIndex}`}
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        sx={{ color: palette.text, mt: 0.5 }}
                                                    >
                                                        {trimmedLine}
                                                    </Typography>
                                                );
                                            }

                                            return (
                                                <Typography key={`${section.title}-${lineIndex}`} variant="body2" color="text.secondary">
                                                    {trimmedLine}
                                                </Typography>
                                            );
                                        })}

                                        {section.image && (
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    mt: 1,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: "1px solid #E4E9EF",
                                                    bgcolor: "white",
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={section.image.src}
                                                    alt={section.image.alt}
                                                    sx={{
                                                        width: "100%",
                                                        maxHeight: 260,
                                                        objectFit: "cover",
                                                        borderRadius: 1.5,
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                    {section.image.caption}
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            </Box>

            <Box sx={{ mt: 6 }}>
                <SectionTitle title="LA LISTE COMPLÈTE (1 À 31)" />
                <Grid container spacing={1.5}>
                    {orderedColistiers.map((item) => {
                        const parsed = parseColistier(item);
                        return (
                        <Grid key={item} size={{ xs: 12, md: 6 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.35,
                                    borderRadius: 2,
                                    border: "1px solid #E4E9EF",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: "#FBFCFE",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        bgcolor: palette.blue,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: "white",
                                            fontWeight: 800,
                                            fontSize: "0.82rem",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {parsed.rank}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                                    {parsed.text}
                                </Typography>
                            </Paper>
                        </Grid>
                        );
                    })}
                </Grid>
            </Box>

            <Paper elevation={1} sx={{ mt: 4, p: 2, borderRadius: 3 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: palette.blue }}>
                    Photo de groupe
                </Typography>
                <Box
                    component="img"
                    src={groupPhoto}
                    alt="Groupe de la liste municipale"
                    sx={{
                        width: "100%",
                        maxHeight: 520,
                        objectFit: "cover",
                        borderRadius: 2,
                    }}
                />
            </Paper>
        </Container>
    );
};

export default CampaignPage;
